import {
  getObject,
  getLeoUuid,
  LeoUUID,
  LeoRPCRequest,
  LeoRPCResponse,
  LeoRPCError,
  LeoRPCResult,
  LeoRPC
} from "@surya-digital/leo-ts-types";
import { RespondentSelection } from "../types/respondentSelection";
/* eslint-disable import/export */
export abstract class UnassignSurveyRPC implements
  LeoRPC<UnassignSurveyRPC.Request, UnassignSurveyRPC.Response, UnassignSurveyRPC.Errors.Errors>
{
  abstract execute(
    request: UnassignSurveyRPC.Request
  ): Promise<LeoRPCResult<UnassignSurveyRPC.Response, UnassignSurveyRPC.Errors.Errors>>;
}

export namespace UnassignSurveyRPC {

  export class InvalidRequestError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidRequestError";
    }
  }

  export interface RequestDTO {
    respondentSelection: RespondentSelection.RespondentSelectionDTO;
    projectId: string;
  }
  
  export class Request implements LeoRPCRequest {
    readonly isUnassignSurveyRPCRequest: boolean = true;
  
    readonly respondentSelection: RespondentSelection.RespondentSelection;
    readonly projectId: LeoUUID;
  
  
    constructor(
    respondentSelection: RespondentSelection.RespondentSelection, 
    projectId: LeoUUID
    ) {
      this.respondentSelection = respondentSelection;
      this.projectId = projectId;
    }
  
    static fromDTO(dto: RequestDTO): Request {
      const respondentSelection = RespondentSelection.fromDTO(getObject(dto, "respondentSelection", InvalidRequestRespondentSelectionError));
      const projectId = getLeoUuid(dto, "projectId", InvalidRequestProjectIdError);
      return new Request(
        respondentSelection,
        projectId
      );
    }
  
    toDTO(): RequestDTO {
      const dto: RequestDTO = {
          respondentSelection: this.respondentSelection.toDTO(),
          projectId: this.projectId.toDTO()
      };
      return dto;
    }
    }
  
  Request.prototype.toString = function toString(): string {
    return `UnassignSurveyRPC(respondentSelection='${this.respondentSelection}',projectId='${this.projectId}')`;
  };
  

  export class InvalidResponseError extends Error {
    constructor(message?: string) {
      super(message);
      Object.setPrototypeOf(this, new.target.prototype);
      this.name = "InvalidResponseError";
    }
  }

  export interface ResponseDTO {}

  export class Response implements LeoRPCResponse {
    static fromDTO(): Response {
      return new Response();
    }

    toDTO(): ResponseDTO {
      return {}
    }
  }

  Response.prototype.toString = function toString(): string {
    return `UnassignSurveyRPC.Response()`;
  };

  export enum RPCError {
    InvalidProjectId = "INVALID_PROJECT_ID",
    ProjectAlreadyArchived = "PROJECT_ALREADY_ARCHIVED",
    DeletedColumnFilter = "DELETED_COLUMN_FILTER",
    InvalidRespondentIdFound = "INVALID_RESPONDENT_ID_FOUND",
    SurveyAlreadySubmitted = "SURVEY_ALREADY_SUBMITTED"
  }

  export namespace Errors {
    
    export interface InvalidProjectIdDTO {
      code: string;
    }
    
    export class InvalidProjectId extends LeoRPCError<RPCError> {
      readonly isInvalidProjectId: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidProjectId;
        super(code);
      }
    
    
      toDTO(): InvalidProjectIdDTO {
        const dto: InvalidProjectIdDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidProjectId.prototype.toString = function toString(): string {
      return `UnassignSurveyRPC.INVALID_PROJECT_ID()`;
    };
    
    
    export interface ProjectAlreadyArchivedDTO {
      code: string;
    }
    
    export class ProjectAlreadyArchived extends LeoRPCError<RPCError> {
      readonly isProjectAlreadyArchived: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.ProjectAlreadyArchived;
        super(code);
      }
    
    
      toDTO(): ProjectAlreadyArchivedDTO {
        const dto: ProjectAlreadyArchivedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    ProjectAlreadyArchived.prototype.toString = function toString(): string {
      return `UnassignSurveyRPC.PROJECT_ALREADY_ARCHIVED()`;
    };
    
    
    export interface DeletedColumnFilterDTO {
      code: string;
    }
    
    export class DeletedColumnFilter extends LeoRPCError<RPCError> {
      readonly isDeletedColumnFilter: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.DeletedColumnFilter;
        super(code);
      }
    
    
      toDTO(): DeletedColumnFilterDTO {
        const dto: DeletedColumnFilterDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    DeletedColumnFilter.prototype.toString = function toString(): string {
      return `UnassignSurveyRPC.DELETED_COLUMN_FILTER()`;
    };
    
    
    export interface InvalidRespondentIdFoundDTO {
      code: string;
    }
    
    export class InvalidRespondentIdFound extends LeoRPCError<RPCError> {
      readonly isInvalidRespondentIdFound: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.InvalidRespondentIdFound;
        super(code);
      }
    
    
      toDTO(): InvalidRespondentIdFoundDTO {
        const dto: InvalidRespondentIdFoundDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    InvalidRespondentIdFound.prototype.toString = function toString(): string {
      return `UnassignSurveyRPC.INVALID_RESPONDENT_ID_FOUND()`;
    };
    
    
    export interface SurveyAlreadySubmittedDTO {
      code: string;
    }
    
    export class SurveyAlreadySubmitted extends LeoRPCError<RPCError> {
      readonly isSurveyAlreadySubmitted: boolean = true;
    
    
      constructor(
      ) {
        const code = RPCError.SurveyAlreadySubmitted;
        super(code);
      }
    
    
      toDTO(): SurveyAlreadySubmittedDTO {
        const dto: SurveyAlreadySubmittedDTO = {
          code: this.code,
        };
        return dto;
      }
    
      }
    
    SurveyAlreadySubmitted.prototype.toString = function toString(): string {
      return `UnassignSurveyRPC.SURVEY_ALREADY_SUBMITTED()`;
    };
    

    export type Errors = InvalidProjectId | ProjectAlreadyArchived | DeletedColumnFilter | InvalidRespondentIdFound | SurveyAlreadySubmitted;
    export type ErrorsDTO = InvalidProjectIdDTO | ProjectAlreadyArchivedDTO | DeletedColumnFilterDTO | InvalidRespondentIdFoundDTO | SurveyAlreadySubmittedDTO;

    export const fromDTO = (dto: ErrorsDTO): Errors => {
      switch (dto.code) {
        case RPCError.InvalidProjectId:
          return new InvalidProjectId();
        case RPCError.ProjectAlreadyArchived:
          return new ProjectAlreadyArchived();
        case RPCError.DeletedColumnFilter:
          return new DeletedColumnFilter();
        case RPCError.InvalidRespondentIdFound:
          return new InvalidRespondentIdFound();
        case RPCError.SurveyAlreadySubmitted:
          return new SurveyAlreadySubmitted();
        default:
          throw new InvalidResponseError(`Unexpected error code: ${dto.code}`);
      }
    };
  }
}

export class InvalidRequestRespondentSelectionError extends UnassignSurveyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestRespondentSelectionError";
  }
}

export class InvalidRequestProjectIdError extends UnassignSurveyRPC.InvalidRequestError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidRequestProjectIdError";
  }
}


export class InvalidResponseInvalidProjectIdError extends UnassignSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidProjectIdError";
  }
}

export class InvalidResponseProjectAlreadyArchivedError extends UnassignSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseProjectAlreadyArchivedError";
  }
}

export class InvalidResponseDeletedColumnFilterError extends UnassignSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseDeletedColumnFilterError";
  }
}

export class InvalidResponseInvalidRespondentIdFoundError extends UnassignSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseInvalidRespondentIdFoundError";
  }
}

export class InvalidResponseSurveyAlreadySubmittedError extends UnassignSurveyRPC.InvalidResponseError {
  constructor(message?: string) {
    super(message);
    Object.setPrototypeOf(this, new.target.prototype);
    this.name = "InvalidResponseSurveyAlreadySubmittedError";
  }
}

